import { Accordion, ContentLoader, Grid, Icon, Scrollbar, DropdownButton, ComponentType } from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import {
  useFetchTransmittalById,
  useFetchTransmittalHistories,
  useFetchTransmittalHistoriesItems,
} from '@transmittal/transmittal.queries';
import './TransmittalHistoryPage.scss';
import {
  DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_XXL,
  DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_LG,
  DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_MD,
} from '@common/constants';
import { TransmittalDetailTabDetailTopBar } from '@transmittal/detailPage/TransmittalDetailTabDetailTopBar';
import { useBreakPoints } from '@common/breakpoints.hooks';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId, useProjectIsArchived } from '@config/config.hooks';
import { GeneralInfoTable } from '@common/GeneralInfoTable';
import { DetailPageLayout } from '@app/DetailPageLayout';
import { DownloadButton } from '@file/DownloadButton';
import {
  exportTransmittalHistoriesExcelAsync,
  exportTransmittalHistoriesPdfAsync,
} from '@transmittal/transmittal.services';
import { ETransmittalType, TransmittalTypeOptions } from '@transmittal/transmittal.constants';
import { getTransmittalItemRoute } from '@transmittal/detailPage/transmittalDetailPage.hooks';
import { Note } from '@core/Note/Note';
import { useFetchTransmittalItemsLatestRevision } from '@transmittal/transmittalItem/transmittalItem.queries';
import { isRestrictedAccessFromApiResponse } from '@common/api.services';
import { getMasterScheduleDetail } from '@masterSchedule/masterSchedule.hooks';
import { IProjectTransmittalHistoryItemResponse } from './transmittalHistory.models';
import { TransmittalItemTable } from './tables/transmittalItem/TransmittalItemTable';
import { TransmittalRefHistory } from './tables/TransmittalRefHistory';
import { TransmittalDistributionTable } from './tables/distribution/TransmittalDistributionTable';

interface ITransmittalHistoryPageProps {}

export const TransmittalHistoryPage: React.FunctionComponent<ITransmittalHistoryPageProps> = () => {
  const isProjectArchived = useProjectIsArchived();

  const { id, masterScheduleId } = useParams();
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [shouldShowRemovedFromUse, setShouldShowRemovedFromUse] = React.useState<boolean>(true);
  const { isXs, isSm, isMd, isXxl } = useBreakPoints();
  const isMobileSize = isSm || isXs || isMd;
  const HistoryPageSize = React.useMemo(() => {
    setPageNumber(1);
    if (isXxl) return DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_XXL;
    if (isMobileSize) return DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_MD;
    return DEFAULT_TRANSMITTAL_HISTORY_PAGE_SIZE_LG;
  }, [isMobileSize, isXxl]);
  const projectId = useProjectId();

  const { navigate } = useNavigate();

  const { masterSchedule } = getMasterScheduleDetail(projectId, Number(masterScheduleId) || -1);

  const {
    data: transmittalHistoriesPageResult,
    isLoading: isLoadingTransmittalHistories,
    isFetching: isFetchingTransmittalHistories,
    error: errorTransmittalHistories,
  } = useFetchTransmittalHistories({
    projectId,
    projectMasterScheduleId: Number(masterScheduleId) || -1,
    projectTransmittalId: Number(id) || -1,
    pageNumber,
    pageSize: HistoryPageSize,
  });

  const {
    data: transmittalItemsLatestRevision,
    isLoading: isLoadingTransmittalItemsLatestRevision,
    isFetching: isFetchingTransmittalItemsLatestRevision,
  } = useFetchTransmittalItemsLatestRevision({
    projectId,
    projectMasterScheduleId: Number(masterScheduleId) || -1,
    projectTransmittalId: Number(id) || -1,
  });

  const firstTransmittalHistroy = React.useMemo(() => {
    return transmittalHistoriesPageResult &&
      transmittalHistoriesPageResult?.items &&
      transmittalHistoriesPageResult.items.length > 0
      ? transmittalHistoriesPageResult.items[0]
      : undefined;
  }, [transmittalHistoriesPageResult]);
  const transmittalFormName = React.useMemo(() => {
    if (!firstTransmittalHistroy || !firstTransmittalHistroy.type) return null;
    return (
      TransmittalTypeOptions.find(
        (transmittalTypeOption) => transmittalTypeOption.code === firstTransmittalHistroy.type,
      )?.formName || null
    );
  }, [firstTransmittalHistroy]);

  const {
    data: transmittalHistoriesItemsResponses,
    isLoading: isLoadingTransmittalHistoriesItems,
    isFetching: isFetchingTransmittalHistoriesItems,
  } = useFetchTransmittalHistoriesItems({
    projectId,
    projectMasterScheduleId: Number(masterScheduleId) || -1,
    shouldShowRemovedFromUse,
  });

  const {
    data: transmittalDetails,
    isLoading: isLoadingTransmittalDetails,
    isFetching: isFetchingTransmittalDetails,
    error: errorGetTransmittalDetails,
  } = useFetchTransmittalById({ projectId }, Number(id));

  const [hoverTransmittalId, setHoverTransmittalId] = React.useState<number | undefined>(undefined);

  const createTransmittalItemTable = (transmittalHistoryItemResponse: IProjectTransmittalHistoryItemResponse) => {
    const filteredItems = transmittalHistoryItemResponse.items.filter((item) => item.type === transmittalDetails?.type);
    return (
      <Accordion
        allowClickHeaderToExpand
        className="gcl-mb-md"
        bodyClassName="gcl-gems-transmittal-history-page-accordion"
        shouldRenderBodyAfterExpanded={false}
        content={{
          id: transmittalHistoryItemResponse.docType.id,
          defaultIsExpanded: (filteredItems.length || 0) > 0,
          onRenderBody: () => (
            <TransmittalItemTable
              transmittalHistoriesPagedResult={transmittalHistoriesPageResult}
              transmittalHistoryItems={filteredItems || []}
              transmittalItemsLatestRevision={transmittalItemsLatestRevision}
              isLoading={isLoadingTransmittalHistories || isLoadingTransmittalHistoriesItems}
              hoverTransmittalId={hoverTransmittalId}
              setHoverTransmittalId={setHoverTransmittalId}
            />
          ),
          onRenderHeader: () => <span>{transmittalHistoryItemResponse.docType.name}</span>,
        }}
      />
    );
  };

  const isRestricted = React.useMemo(() => {
    return isRestrictedAccessFromApiResponse([errorTransmittalHistories]);
  }, [JSON.stringify(errorTransmittalHistories)]);

  return (
    <Scrollbar>
      <DetailPageLayout
        errorGetDetail={errorGetTransmittalDetails}
        isRestricted={isRestricted}
        data={{ transmittalHistoriesPageResult, firstTransmittalHistroy, transmittalHistoriesItemsResponses }}
        isRefetching={
          isFetchingTransmittalHistories || isFetchingTransmittalHistoriesItems || isFetchingTransmittalDetails
        }
        isLoading={isLoadingTransmittalHistories || isLoadingTransmittalHistoriesItems || isLoadingTransmittalDetails}
        title={(DetailPageRightMenuOpener?: React.ReactElement, InstructionModalOpener?: React.ReactElement) => {
          return (
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item flexGrow={1}>
                {`${transmittalFormName || 'Transmittal'} History`}
              </Grid>
              <Grid item>
                <div className="gcl-gems-detail-page-layout-title-button">{InstructionModalOpener}</div>
              </Grid>
            </Grid>
          );
        }}
        breadcrumb={[
          {
            text: 'Home',
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.home.path);
            },
          },
          {
            text: `Search ${transmittalFormName ?? 'Transmittal'}`,
            prefixIconName: 'Search',
            onClick: () => {
              switch (firstTransmittalHistroy?.type) {
                case ETransmittalType.DESIGNER:
                  navigate(routes.transmittals.designerTransmittal.path);
                  break;
                case ETransmittalType.SITE:
                  navigate(routes.transmittals.siteTransmittal.path);
                  break;
                case ETransmittalType.ICE:
                  navigate(routes.transmittals.checkerTransmittal.path);
                  break;
                case ETransmittalType.REVIEWER:
                  navigate(routes.transmittals.lirTransmittal.path);
                  break;
                default:
                  navigate(routes.transmittals.path);
                  break;
              }
            },
          },
          {
            text: `View ${transmittalFormName ?? 'Transmittal'}`,
            prefixIconName: 'FileText',
            onClick: () => {
              navigate(getTransmittalItemRoute(firstTransmittalHistroy?.type), { id: (id || 0).toString() });
            },
          },
          {
            text: 'History',
            active: true,
          },
        ]}
        render={(DetailPageRightMenuOpener) => {
          return (
            <Grid container rowSpacing={1} className="gcl-gems-transmittal-history-page-container">
              <Grid item xs={12}>
                <GeneralInfoTable
                  items={[
                    {
                      title: (
                        <>
                          From <Icon iconName="ArrowRight" /> To
                        </>
                      ),
                      content: (
                        <ContentLoader
                          active={isLoadingTransmittalHistories || !firstTransmittalHistroy}
                          paragraphConfigs={[
                            { suffixBlankRow: false, numberOfRows: 1, lineHeight: 18, linePadding: 0 },
                          ]}
                        >
                          {firstTransmittalHistroy?.sendFromProjectCompanyName} <Icon iconName="ArrowRight" />{' '}
                          {firstTransmittalHistroy?.sendToProjectCompanyName}
                        </ContentLoader>
                      ),
                    },
                  ]}
                />
              </Grid>

              <Grid item xs={12}>
                <TransmittalDetailTabDetailTopBar
                  isReadOnly={isProjectArchived || masterSchedule?.isCancelled || masterSchedule?.isLocked}
                  masterScheduleId={Number(masterScheduleId) || 0}
                  renderExportExcelButton={(excelIcon) => (
                    <DownloadButton
                      exportFunction={exportTransmittalHistoriesExcelAsync}
                      defaultExportProps={{
                        projectId,
                        projectMasterScheduleId: Number(masterScheduleId) || 0,
                        transmittalType: String(firstTransmittalHistroy?.type),
                        shouldShowRemovedFromUse,
                      }}
                      buttonRender={(exportFile) => (
                        <div
                          onClick={() => {
                            exportFile();
                          }}
                        >
                          {excelIcon}
                        </div>
                      )}
                    />
                  )}
                  renderExportPdfButton={(pdfIcon) => (
                    <DownloadButton
                      exportFunction={exportTransmittalHistoriesPdfAsync}
                      defaultExportProps={{
                        projectId,
                        projectMasterScheduleId: Number(masterScheduleId) || 0,
                        shouldIncludeCoverPage: false,
                      }}
                      buttonRender={(exportFile) => {
                        return (
                          <DropdownButton
                            type={ComponentType.Secondary}
                            className="gcl-gems-transmittal-history-export-pdf-button"
                            mainButtonContent={pdfIcon}
                            dropdownItems={[
                              <div
                                key="gcl-gems-transmittal-history-export-pdf-button-without-cover-page"
                                onClick={() => {
                                  exportFile({ shouldIncludeCoverPage: false });
                                }}
                              >
                                Without Cover Sheet
                              </div>,
                              <div
                                key="gcl-gems-transmittal-history-export-pdf-button-with-cover-page"
                                onClick={() => {
                                  exportFile({ shouldIncludeCoverPage: true });
                                }}
                              >
                                With Cover Sheet
                              </div>,
                            ]}
                          />
                        );
                      }}
                    />
                  )}
                  onSearch={(searchText) => {}}
                  shouldShowRemovedItems={shouldShowRemovedFromUse}
                  onClickShowHideRemovedItems={(shouldShow) => {
                    setShouldShowRemovedFromUse(shouldShow);
                  }}
                />
                {(isXs || isSm) && (
                  <Note
                    componentType={ComponentType.Info}
                    className="gcl-mb-md"
                    prefixIcon={<Icon iconName="Lightbulb" />}
                  >
                    Use desktop computer to ensure best experience
                  </Note>
                )}

                <Accordion
                  allowClickHeaderToExpand
                  className="gcl-mb-md gcl-gems-transmittal-history-page-transmittal-ref-history-accordion"
                  bodyClassName="gcl-gems-transmittal-history-page-transmittal-ref-history-body"
                  shouldRenderBodyAfterExpanded={false}
                  content={{
                    id: 'transmittal-ref-history',
                    defaultIsExpanded: true,
                    onRenderBody: () => (
                      <TransmittalRefHistory
                        firstTransmittalHistory={firstTransmittalHistroy}
                        transmittalHistoryPagedResult={transmittalHistoriesPageResult}
                        onPageChanged={(newPageNumber: number, pageSize: number) => {
                          setPageNumber(newPageNumber);
                        }}
                        isLoading={isLoadingTransmittalHistories || isFetchingTransmittalHistoriesItems}
                        hoverTransmittalId={hoverTransmittalId}
                        setHoverTransmittalId={setHoverTransmittalId}
                      />
                    ),
                  }}
                />
                {isLoadingTransmittalHistories || isFetchingTransmittalHistoriesItems ? (
                  <></>
                ) : (
                  <>
                    {transmittalHistoriesItemsResponses?.map((transmittalHistoriesItemsResponse) =>
                      createTransmittalItemTable(transmittalHistoriesItemsResponse),
                    )}
                    <Accordion
                      allowClickHeaderToExpand
                      className="gcl-mb-md"
                      bodyClassName="gcl-gems-transmittal-history-page-accordion"
                      content={{
                        id: 'Distribution',
                        defaultIsExpanded: true,
                        onRenderBody: () => (
                          <TransmittalDistributionTable
                            transmittalHistoriesPagedResult={transmittalHistoriesPageResult}
                            isLoading={isLoadingTransmittalHistories || isLoadingTransmittalHistoriesItems}
                            hoverTransmittalId={hoverTransmittalId}
                            setHoverTransmittalId={setHoverTransmittalId}
                          />
                        ),
                        onRenderHeader: 'Distribution',
                      }}
                    />
                  </>
                )}
              </Grid>
            </Grid>
          );
        }}
      />
    </Scrollbar>
  );
};
